import {useParams, useSearchParams} from 'react-router'
import {fromJust, mthen} from '@freckle/maybe'
import {Languages} from '@freckle/educator-entities/ts/common/helpers/languages'
import {parseQueryParamNumArray} from '@freckle/educator-entities/ts/common/helpers/routers/query-params'
import {useAsync, useAsyncOnceDefined} from '@freckle/educator-entities/ts/common/hooks/use-async'
import {useSchools} from '@freckle/educator-entities/ts/common/hooks/use-schools'
import {useTeachersAsASchoolAdmin} from '@freckle/educator-entities/ts/common/hooks/use-teacher-as-a-school-admin'
import {fetchRlDomains} from '@freckle/educator-entities/ts/common/models/rl-standard'
import {fetchRlStandardSets} from '@freckle/educator-entities/ts/common/models/rl-standard-set'
import {WithResources} from '@freckle/school/ts/common/components/with-resources'
import {useSchoolAdmin} from '@freckle/school/ts/hooks/use-school-admin'
import {useStudents} from '@freckle/school/ts/hooks/use-students'
import {TeacherProficiencyReport} from '@freckle/school/ts/proficiency-report/components/teacher/teacher-proficiency-report'
import {fetchProficiencyReportsForSchools} from '@freckle/school/ts/proficiency-report/models/proficiency-report'
import {indexStudentsById} from '@freckle/school/ts/users/collections/students'

export default function TeacherProficiencyReportContainer() {
  const {pathTeacherId} = useParams()
  const [searchParams, _setSearchParams] = useSearchParams()
  const grade = searchParams.get('grade')
  const domainId = searchParams.get('domainId')
  const schoolIds = searchParams.get('schoolsIds')
  const selectedGrade = parseInt(fromJust(grade, 'Cannot find grade in params'), 10)
  const selectedDomainId = fromJust(domainId, 'Cannot find domainId in params')
  const selectedSchoolIds = parseQueryParamNumArray(
    fromJust(schoolIds, 'Cannot find schoolsIds in params')
  )
  const teacherId = parseInt(fromJust(pathTeacherId, 'Cannot find pathTeacherId in URL'), 10)
  const students = useStudents()
  const teachers = useTeachersAsASchoolAdmin()
  const schoolAdmin = useSchoolAdmin()
  const schools = useSchools()

  const proficiencyReportCollection = useAsync(
    () =>
      fetchProficiencyReportsForSchools(selectedSchoolIds, {
        grade: selectedGrade,
        domainId: selectedDomainId,
        teacherId
      }),
    'fetchProficiencyReportsForSchools',
    [
      selectedSchoolIds,
      {
        grade: selectedGrade,
        domainId: selectedDomainId,
        teacherId
      }
    ]
  )

  const domains = useAsyncOnceDefined(
    ({schoolAdmin}) => fetchRlDomains('math', schoolAdmin.mathStandardSet, Languages.En),
    'fetchRlDomains',
    ['math', mthen(schoolAdmin.data, sa => sa.mathStandardSet), Languages.En],
    {schoolAdmin}
  )
  const mathStandardSet = useAsyncOnceDefined(
    ({schoolAdmin}) =>
      fetchRlStandardSets({
        'content-area': 'math',
        'standard-set': schoolAdmin.mathStandardSet.id
      }).then(standardSets => standardSets[0] ?? null),
    'fetchRlStandardSet',
    ['math', mthen(schoolAdmin.data, sa => sa.mathStandardSet.id)],
    {schoolAdmin}
  )

  return (
    <WithResources
      resources={{
        students,
        teachers,
        schools,
        proficiencyReportCollection,
        domains,
        mathStandardSet
      }}
      render={({
        students,
        teachers,
        schools,
        proficiencyReportCollection,
        domains,
        mathStandardSet
      }) => {
        const teacher = fromJust(
          teachers.find(teacher => teacher.id === teacherId),
          `Could not find teacher with id ${teacherId}`
        )
        const domain = fromJust(
          domains.find(domain => domain.id === selectedDomainId),
          `Could not find domain with id ${selectedDomainId}`
        )
        return (
          <TeacherProficiencyReport
            students={indexStudentsById(students)}
            schools={schools}
            proficiencyReportData={proficiencyReportCollection}
            teacher={teacher}
            domain={domain}
            selectedSchoolsIds={selectedSchoolIds}
            selectedGrade={selectedGrade}
            mathStandardSet={mathStandardSet!}
          />
        )
      }}
    />
  )
}
